<script lang="ts" setup>
import { AbcIcon } from "@ordbogen-npm/abc-dsk";
import { round } from "lodash-es";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import TgProgress from "@/abc-dsk/components/Progress/TgProgress.vue";

const props = defineProps<{
  isFinished: boolean;
  total: number;
  finishedRound: number;
  checkpoints: number[];
  checkpointActive: boolean;
}>();

const { t } = useI18n();

const checkpointsWithPosition = computed<{ value: number; position: number }[]>(() =>
  props.checkpoints.map((checkpoint, i) => ({
    value: checkpoint,
    position: round(((i + 1) / (props.checkpoints.length + 1)) * 100, 2)
  }))
);

const closestCheckpoint = computed(() =>
  checkpointsWithPosition.value.reduce(function (prev, curr) {
    return Math.abs(curr.value - props.finishedRound) < Math.abs(prev.value - props.finishedRound)
      ? curr
      : prev;
  })
);

const progression = ref<number>(0);
watch(
  () => props.finishedRound,
  (val) => {
    if (props.checkpointActive && closestCheckpoint.value) {
      // This makes sure the progress don't overshoot the checkpoint,
      // when it's active. If the checkpoint position in "finishedRound" unit is less
      // than the finished round, we take that value.
      // E.g. 6/17 = 35,29% progression, but checkpoint is at 33%.

      progression.value = Math.min(
        val,
        round((closestCheckpoint.value.position / 100) * props.total, 2)
      );
    } else {
      progression.value = props.finishedRound;
    }
  },
  { immediate: true }
);

const showFinishIcon = ref(props.isFinished);
watch(
  () => props.isFinished,
  (value) => {
    if (!value) {
      return;
    }

    showFinishIcon.value = true;
  }
);
</script>

<template>
  <div class="quiz-progress-top">
    <span class="quiz-progress-top__label">
      {{ t("vocab.games.footer.completedProgressLabel") }}
    </span>
    <span>
      <span class="quiz-progress-top__progress">{{ finishedRound }}</span
      >/{{ total }}
    </span>
  </div>
  <div class="quiz-progress-bottom">
    <TgProgress
      :value="progression"
      :max="total"
      :duration="500"
      :screen-reader-label="t('vocab.games.footer.progressBarLabel')"
    />
    <span
      v-for="(checkpoint, index) in checkpointsWithPosition"
      :key="index"
      class="quiz-progress__checkpoint"
      :class="{
        'quiz-progress__checkpoint--primary': finishedRound > checkpoint.value
      }"
      :style="{ left: `${checkpoint.position}%` }"
    >
      <AbcIcon name="flag" />
    </span>
    <span class="icon-finish" :class="{ 'icon-finish--primary': showFinishIcon }">
      <AbcIcon name="check-mark" />
    </span>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

@icon-size: 30px;
@transition-delay: 450ms; // Start transition just before the animation of the progress bar is done (@ 500ms)

.quiz-progress-top {
  display: none;
  margin-bottom: 5px;
  font-size: 1rem;
  line-height: 1.5rem;

  #media-breakpoint-up(md, {
    display: flex;
    justify-content: space-between;
  });

  &__label {
    font-weight: 700;
  }

  &__progress {
    color: rgb(@theme-colors[@primary][base]);
  }
}

.quiz-progress-bottom {
  display: flex;
  position: relative;
  align-items: center;
  min-height: @icon-size;
}

.quiz-progress__checkpoint {
  display: flex;
  position: absolute;
  z-index: 1;
  align-items: center;
  justify-content: center;
  width: @icon-size;
  height: @icon-size;
  margin-left: -1 * (@icon-size / 2);
  padding: 3px;
  transition: all 150ms ease-in-out @transition-delay;
  border-radius: @icon-size;
  background: rgb(@theme-colors[@grey][25]);
  color: rgb(@theme-colors[@grey][50]);

  &--primary {
    background: rgb(@theme-colors[@primary][base]);
    color: @colors[@white];
  }
}

.icon-finish {
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  align-items: center;
  justify-content: center;
  width: @icon-size;
  height: @icon-size;
  transition: all 150ms ease-in-out @transition-delay;
  border-radius: @icon-size;
  background: rgb(@theme-colors[@grey][25]);
  color: rgb(@theme-colors[@grey][50]);

  &--primary {
    background: rgb(@theme-colors[@primary][base]);
    color: @colors[@white];
  }
}
</style>
